/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  EnvironmentProviders,
  inject,
  InjectionToken,
  Injector,
  makeEnvironmentProviders,
  signal,
  Type
} from '@angular/core';
import { LocalStorageService } from '@iot-platform/core';
import { FilterEngineMode, ToolbarPageType } from '@iot-platform/iot-platform-ui';
import { AbstractAuthFacade, CommonBaseFacade } from '@iot-platform/models/common';
import { Store } from '@ngrx/store';
import { FeatureMasterViewSettingsFactory } from '../models/master-view-settings-factory.model';
import { FeatureMasterViewDrawerDetailsLayout, FeatureMasterViewSettings } from '../models/master-view-settings.model';
import { AbstractMasterViewDashboardService } from '../services/abstract-master-view-dashboard.service';
import { AbstractMasterViewMapService } from '../services/abstract-master-view-map.service';
import { AbstractMasterViewService } from '../services/abstract-master-view.service';

export const FEATURE_MASTER_VIEW_SETTINGS = new InjectionToken<FeatureMasterViewSettings[]>('FEATURE_MASTER_VIEW_SETTINGS');
export const FEATURE_MASTER_VIEW_SERVICE = new InjectionToken<AbstractMasterViewService[]>('FEATURE_MASTER_VIEW_SERVICE');
export const FEATURE_MASTER_VIEW_AUTH_FACADE = new InjectionToken<AbstractAuthFacade[]>('FEATURE_MASTER_VIEW_AUTH_FACADE');
export const FEATURE_MASTER_VIEW_FACADE = new InjectionToken<CommonBaseFacade[]>('FEATURE_MASTER_VIEW_FACADE');
export const FEATURE_MASTER_VIEW_DASHBOARD_SERVICE = new InjectionToken<AbstractMasterViewDashboardService[]>('FEATURE_MASTER_VIEW_DASHBOARD_SERVICE');
export const FEATURE_MASTER_VIEW_MAP_SERVICE = new InjectionToken<AbstractMasterViewMapService[]>('FEATURE_MASTER_VIEW_MAP_SERVICE');

export const FEATURE_MASTER_VIEW_SETTINGS_FACTORY = new InjectionToken<
  {
    masterViewService: Type<AbstractMasterViewService>;
    authFacade: Type<AbstractAuthFacade>;
    masterViewFacade?: Type<CommonBaseFacade>;
    settingsFactory: FeatureMasterViewSettingsFactory;
    masterViewDashboardService?: Type<AbstractMasterViewDashboardService>;
    masterViewMapService?: Type<AbstractMasterViewMapService>;
  }[]
>('FEATURE_MASTER_VIEW_SETTINGS_FACTORY');

export const getFeatureMasterViewSettings = (settings: Partial<FeatureMasterViewSettings>) => {
  const fullSettings: FeatureMasterViewSettings = {
    masterViewName: settings.masterViewName,
    additionalOptions: settings.additionalOptions ?? signal(null),
    grid: {
      staticDefinitionType: settings?.grid?.staticDefinitionType ?? signal(false),
      hidePaginator: settings?.grid?.hidePaginator ?? signal(false),
      style: settings?.grid?.style ?? signal(null),
      autoScroll: settings?.grid?.autoScroll ?? signal(false),
      apiHandlerService: settings?.grid?.apiHandlerService,
      masterDetails: {
        enabled: settings?.grid?.masterDetails?.enabled ?? signal(false),
        rowAutoHeight: settings?.grid?.masterDetails?.rowAutoHeight ?? signal(false),
        componentRef: settings?.grid?.masterDetails?.componentRef ?? signal(null)
      }
    },
    toolbar: {
      enabled: settings?.toolbar?.enabled ?? signal(true),
      toolbarName: settings?.toolbar?.toolbarName ?? signal(''),
      withFavoriteViews: settings?.toolbar?.withFavoriteViews ?? signal(true),
      withGrids: settings?.toolbar?.withGrids ?? signal(true),
      pageTypeOptions: {
        visible: settings?.toolbar?.pageTypeOptions?.visible ?? signal(false),
        defaultPageType: settings?.toolbar?.pageTypeOptions?.defaultPageType ?? signal(ToolbarPageType.GRID),
        options: settings?.toolbar?.pageTypeOptions?.options ?? signal([])
      }
    },
    sectionHeader: {
      enabled: settings?.sectionHeader?.enabled ?? signal(false),
      title: settings?.sectionHeader?.title ?? signal(null),
      icon: settings?.sectionHeader?.icon ?? signal(null),
      svgIcon: settings?.sectionHeader?.svgIcon ?? signal(null),
      cssClassName: settings?.sectionHeader?.cssClassName ?? signal(null),
      styles: settings?.sectionHeader?.styles ?? signal(null),
      actions: settings?.sectionHeader?.actions ?? []
    },
    filterEngine: {
      enabled: settings?.filterEngine?.enabled ?? signal(true),
      displayActionButtons: settings?.filterEngine?.displayActionButtons ?? signal(true),
      withFavoriteFilters: settings?.filterEngine?.withFavoriteFilters ?? signal(true),
      suppressOperator: settings?.filterEngine?.suppressOperator ?? signal(true),
      clearOnDestroy: settings?.filterEngine?.clearOnDestroy ?? signal(false),
      mode: settings?.filterEngine?.mode ?? signal(FilterEngineMode.CLASSIC)
    },
    drawerDetails: {
      enabled: settings?.drawerDetails?.enabled ?? signal(false),
      position: settings?.drawerDetails?.position ?? signal('start'),
      mode: settings?.drawerDetails?.mode ?? signal('side'),
      opened: settings?.drawerDetails?.opened ?? signal(false),
      openedOnDestroy: settings?.drawerDetails?.openedOnDestroy ?? signal(false),
      hasBackdrop: settings?.drawerDetails?.hasBackdrop ?? signal(false),
      closeOnBackdropClick: settings?.drawerDetails?.closeOnBackdropClick ?? signal(false),
      customOpeningEventType: settings?.drawerDetails?.customOpeningEventType ?? signal(null),
      componentRef: settings?.drawerDetails?.componentRef ?? signal(null),
      layout: settings?.drawerDetails?.layout ?? signal(FeatureMasterViewDrawerDetailsLayout.GRID),
      width: settings?.drawerDetails?.width ?? signal('30rem'),
      minWidth: settings?.drawerDetails?.minWidth ?? signal('30%'),
      maxWidth: settings?.drawerDetails?.maxWidth ?? signal('70%'),
      resizable: settings?.drawerDetails?.resizable ?? signal(true),
      suppressRowGroupClickEvent: settings?.drawerDetails?.suppressRowGroupClickEvent ?? signal(false)
    },
    dashboard: {
      enabled: settings?.dashboard?.enabled ?? signal(false)
    },
    map: {
      enabled: settings?.map?.enabled ?? signal(false)
    }
  };

  return fullSettings;
};

export function provideFeatureMasterViewSettingsFactory(settings: {
  masterViewService: Type<AbstractMasterViewService>;
  authFacade: Type<AbstractAuthFacade>;
  masterViewFacade?: Type<CommonBaseFacade>;
  settingsFactory: FeatureMasterViewSettingsFactory;
  masterViewDashboardService?: Type<AbstractMasterViewDashboardService>;
  masterViewMapService?: Type<AbstractMasterViewMapService>;
}): EnvironmentProviders {
  const providers = [
    // 1. Explicitly register the concrete classes (creates the single instance)
    { provide: settings.masterViewService, useClass: settings.masterViewService },
    { provide: settings.authFacade, useClass: settings.authFacade },
    { provide: settings.masterViewFacade, useClass: settings.masterViewFacade },
    ...(settings.masterViewDashboardService ? [{ provide: settings.masterViewDashboardService, useClass: settings.masterViewDashboardService }] : []),
    ...(settings.masterViewMapService ? [{ provide: settings.masterViewMapService, useClass: settings.masterViewMapService }] : []),

    // 2. Map existing singletons into the multi tokens (minification safe arrays)
    {
      provide: FEATURE_MASTER_VIEW_SETTINGS_FACTORY,
      useValue: settings,
      multi: true
    },
    {
      provide: FEATURE_MASTER_VIEW_SERVICE,
      useExisting: settings.masterViewService,
      multi: true
    },
    {
      provide: FEATURE_MASTER_VIEW_AUTH_FACADE,
      useExisting: settings.authFacade,
      multi: true
    },
    {
      provide: FEATURE_MASTER_VIEW_FACADE,
      useExisting: settings.masterViewFacade,
      multi: true
    },
    settings.masterViewDashboardService
      ? { provide: FEATURE_MASTER_VIEW_DASHBOARD_SERVICE, useExisting: settings.masterViewDashboardService, multi: true }
      : { provide: FEATURE_MASTER_VIEW_DASHBOARD_SERVICE, useValue: {}, multi: true },
    settings.masterViewMapService
      ? { provide: FEATURE_MASTER_VIEW_MAP_SERVICE, useExisting: settings.masterViewMapService, multi: true }
      : { provide: FEATURE_MASTER_VIEW_MAP_SERVICE, useValue: {}, multi: true },

    // 3. Simplified Factory: Just inject the explicit Types!
    {
      provide: FEATURE_MASTER_VIEW_SETTINGS,
      multi: true,
      useFactory: () => {
        const store = inject(Store);
        const storage = inject(LocalStorageService);
        const authFacade = inject(settings.authFacade);
        const masterViewFacade = inject(settings.masterViewFacade);
        const masterViewService = inject(settings.masterViewService);

        const factorySettings = settings.settingsFactory({
          store,
          storage,
          authFacade,
          masterViewFacade,
          masterViewService
        });

        return getFeatureMasterViewSettings(factorySettings);
      }
    }
  ];

  return makeEnvironmentProviders(providers);
}

export const getMasterViewInjectionTokens = (masterView: string) => {
  // Instead of querying multi arrays and running find(), we query the Injector directly
  const injector = inject(Injector);
  const allSettings: FeatureMasterViewSettings[] = injector.get(FEATURE_MASTER_VIEW_SETTINGS);
  const allSettingsFactories: {
    masterViewService: Type<AbstractMasterViewService>;
    authFacade: Type<AbstractAuthFacade>;
    masterViewFacade?: Type<CommonBaseFacade>;
    settingsFactory: FeatureMasterViewSettingsFactory;
    masterViewDashboardService?: Type<AbstractMasterViewDashboardService>;
    masterViewMapService?: Type<AbstractMasterViewMapService>;
  }[] = injector.get(FEATURE_MASTER_VIEW_SETTINGS_FACTORY);
  const storage = injector.get(LocalStorageService);
  const store = injector.get(Store);

  const settingsFactories = allSettingsFactories.map((sf) => {
    // 💡 MINIFICATION SAFE: We ask the injector for the specific Type!
    // No instanceof, no string matching, no array searching.
    const masterViewService = sf.masterViewService ? injector.get(sf.masterViewService) : injector.get(FEATURE_MASTER_VIEW_SERVICE)[0];
    const masterViewFacade = sf.masterViewFacade ? injector.get(sf.masterViewFacade) : injector.get(FEATURE_MASTER_VIEW_FACADE)[0];
    const authFacade = sf.authFacade ? injector.get(sf.authFacade) : injector.get(FEATURE_MASTER_VIEW_AUTH_FACADE)[0];

    const masterViewDashboardService = sf.masterViewDashboardService
      ? injector.get(sf.masterViewDashboardService)
      : injector.get(FEATURE_MASTER_VIEW_DASHBOARD_SERVICE)[0];

    const masterViewMapService = sf.masterViewMapService ? injector.get(sf.masterViewMapService) : injector.get(FEATURE_MASTER_VIEW_MAP_SERVICE)[0];

    // Evaluate settings logic
    const { masterViewName } = sf.settingsFactory({
      store,
      storage,
      authFacade,
      masterViewFacade,
      masterViewService
    });

    return {
      masterViewName: masterViewName(),
      settings: allSettings.find((item) => item.masterViewName() === masterViewName()) ?? allSettings[0],
      masterViewService,
      masterViewFacade,
      authFacade,
      masterViewDashboardService,
      masterViewMapService
    };
  });

  const settingsFactory = settingsFactories.find(({ masterViewName }) => masterViewName === masterView);
  return settingsFactory ?? settingsFactories[0];
};
